import { useSubscribeWebPushMutation } from "@/common/api/notification/use-subscribe-web-push.mutation";
import { useUnsubscribeWebPushMutation } from "@/common/api/notification/use-unsubscribe-web-push.mutation";
import { useEffect, useState } from "react";
import { isWebPushSupported } from "./_helper/is-web-push-supported";
import { syncWebPushSubscription } from "./_helper/sync-web-push-subscription";

type WebPushPermissionStatus = "unsupported" | NotificationPermission;

interface UseWebPushSubscriptionResult {
  status: WebPushPermissionStatus;
  requestPermission: () => Promise<void>;
}

export function useWebPushSubscription({ enabled }: { enabled: boolean }): UseWebPushSubscriptionResult {
  const { mutateAsync: subscribe } = useSubscribeWebPushMutation();
  const { mutateAsync: unsubscribe } = useUnsubscribeWebPushMutation();

  const supported = isWebPushSupported();
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    supported ? Notification.permission : "default",
  );

  useEffect(() => {
    if (!enabled || !supported) {
      return;
    }

    let cancelled = false;
    void syncWebPushSubscription({
      permission,
      subscribe: (subscription) => subscribe(subscription),
      unsubscribe: (endpoint) => unsubscribe(endpoint),
      isCancelled: () => cancelled,
    }).catch(() => undefined);

    // Re-run when the user grants or revokes the notification permission at runtime.
    let permissionStatus: PermissionStatus | undefined;
    const onPermissionChange = (): void => setPermission(Notification.permission);
    if ("permissions" in navigator) {
      navigator.permissions
        .query({ name: "notifications" as PermissionName })
        .then((status) => {
          if (cancelled) {
            return;
          }
          permissionStatus = status;
          status.onchange = onPermissionChange;
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [enabled, permission, subscribe, supported, unsubscribe]);

  const requestPermission = async (): Promise<void> => {
    if (!supported) {
      return;
    }

    const nextPermission =
      Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
    setPermission(nextPermission);
  };

  return { status: supported ? permission : "unsupported", requestPermission };
}
